import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { useSeoMeta } from '@unhead/react';
import { BookOpen, Star } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { ArticleCard } from '@/components/ArticleCard';
import { PublicationCard } from '@/components/PublicationCard';
import { OfflineLibrarySection } from '@/components/OfflineLibrarySection';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePublicationsByAuthor } from '@/hooks/usePublications';
import { useStarredArticlesByAuthor } from '@/hooks/useStarredArticles';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { WIKI_ARTICLE_KIND, isValidWikiArticle } from '@/lib/nip54';

interface ProfilePageProps {
  pubkey: string;
}

export function ProfilePage({ pubkey }: ProfilePageProps) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? `${pubkey.slice(0, 8)}…`;
  const npub = nip19.npubEncode(pubkey);

  const { data: articles, isLoading } = useQuery({
    queryKey: ['wiki', 'by-author', pubkey],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);
      const events = await nostr.query(
        [{ kinds: [WIKI_ARTICLE_KIND], authors: [pubkey], limit: 100 }],
        { signal },
      );
      const valid = events.filter(isValidWikiArticle);

      // Dedupe by `d` tag, keep the newest.
      const byD = new Map<string, (typeof valid)[number]>();
      for (const event of valid) {
        const d = event.tags.find(([n]) => n === 'd')?.[1] ?? '';
        const existing = byD.get(d);
        if (!existing || event.created_at > existing.created_at) byD.set(d, event);
      }
      return [...byD.values()].sort((a, b) => b.created_at - a.created_at);
    },
    staleTime: 60_000,
  });

  const { data: publications, isLoading: publicationsLoading } = usePublicationsByAuthor(pubkey);
  const { data: starredArticles, isLoading: starredLoading } = useStarredArticlesByAuthor(pubkey);

  useSeoMeta({
    title: `${displayName} — Wikistar`,
    description: metadata?.about,
  });

  const picture = sanitizeUrl(metadata?.picture);
  const banner = sanitizeUrl(metadata?.banner);

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-8">
        <div className="mb-8 overflow-hidden rounded-xl border border-border">
          <div
            className="h-32 bg-gradient-to-br from-primary/20 to-primary/5"
            style={banner ? { backgroundImage: `url(${banner})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
          />
          <div className="flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center">
            <Avatar className="size-16 -mt-14 border-4 border-background sm:-mt-0">
              <AvatarImage src={picture} alt={displayName} />
              <AvatarFallback className="text-xl">{displayName.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h1 className="truncate text-xl font-bold">{displayName}</h1>
              {metadata?.about && <p className="mt-1 text-sm text-muted-foreground break-words">{metadata.about}</p>}
              <p className="mt-1 truncate font-mono text-xs text-muted-foreground">{npub}</p>
            </div>
          </div>
        </div>

        <h2 className="mb-4 flex items-center gap-2 text-xl font-bold tracking-tight">
          <BookOpen className="size-5" />
          Articles by {displayName}
        </h2>

        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-40 w-full" />
            ))}
          </div>
        ) : articles && articles.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {articles.map((event) => (
              <ArticleCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="mx-auto max-w-sm text-muted-foreground">
                {displayName} hasn't written any wiki articles yet.
              </p>
            </CardContent>
          </Card>
        )}

        <h2 className="mb-4 mt-10 flex items-center gap-2 text-xl font-bold tracking-tight">
          <BookOpen className="size-5" />
          Publications by {displayName}
        </h2>

        {publicationsLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-40 w-full" />
            ))}
          </div>
        ) : publications && publications.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {publications.map((event) => (
              <PublicationCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="mx-auto max-w-sm text-muted-foreground">
                {displayName} hasn't published any publications yet.
              </p>
            </CardContent>
          </Card>
        )}

        <h2 className="mb-4 mt-10 flex items-center gap-2 text-xl font-bold tracking-tight">
          <Star className="size-5" />
          Starred by {displayName}
        </h2>

        {starredLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-40 w-full" />
            ))}
          </div>
        ) : starredArticles && starredArticles.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {starredArticles.map((event) => (
              <ArticleCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="mx-auto max-w-sm text-muted-foreground">
                {displayName} hasn't starred any wiki articles yet.
              </p>
            </CardContent>
          </Card>
        )}

        {/* The offline library lives in this browser's localStorage and
            is never published to Nostr, so it isn't tied to any Nostr
            identity — it's only meaningful (and only shown) on the
            viewer's own profile. */}
        {user?.pubkey === pubkey && <OfflineLibrarySection />}

        <p className="mt-8 text-center">
          <Link to="/" className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground">
            Back to home
          </Link>
        </p>
      </div>
    </Layout>
  );
}
